import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ContentQueueProcessor } from "./ContentQueueProcessor";

interface OnboardingStatus {
  onboarding_status: string | null;
  onboarding_progress: number | null;
  onboarding_total: number | null;
  onboarding_current_step: string | null;
  onboarding_started_at: string | null;
  onboarding_completed_at: string | null;
  updated_at?: string | null;
}

interface OnboardingBannerProps {
  siteId: string;
}

export function OnboardingBanner({ siteId }: OnboardingBannerProps) {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasQueueItems, setHasQueueItems] = useState(false);

  useEffect(() => {
    // Fetch initial status
    const fetchStatus = async () => {
      const { data, error } = await supabase
        .from("sites")
        .select(
          "onboarding_status, onboarding_progress, onboarding_total, onboarding_current_step, onboarding_started_at, onboarding_completed_at, updated_at"
        )
        .eq("id", siteId)
        .single();

      if (!error && data) {
        setStatus(data);
        // Only show banner if actively onboarding (has started) or in_progress
        // Don't show for "pending" if never started (onboarding_started_at is null)
        const shouldShow = 
          data.onboarding_status === "in_progress" ||
          (data.onboarding_status === "pending" && data.onboarding_started_at !== null);
        setIsVisible(shouldShow);
      }

      // Check for pending queue items
      const { count } = await supabase
        .from("content_generation_queue")
        .select("id", { count: "exact", head: true })
        .eq("site_id", siteId)
        .in("status", ["pending", "processing"]);

      setHasQueueItems((count || 0) > 0);
    };

    fetchStatus();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`onboarding-${siteId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "sites",
          filter: `id=eq.${siteId}`,
        },
        (payload) => {
          const newStatus = payload.new as OnboardingStatus;
          setStatus(newStatus);
          
          if (newStatus.onboarding_status === "completed") {
            // Show completion briefly then hide
            setTimeout(() => setIsVisible(false), 5000);
          } else if (newStatus.onboarding_status === "failed") {
            // Keep visible for failed status
            setIsVisible(true);
          } else if (newStatus.onboarding_status === "in_progress") {
            setIsVisible(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [siteId]);

  // If there are queue items, show the queue processor instead
  if (hasQueueItems) {
    return (
      <ContentQueueProcessor 
        siteId={siteId} 
        onComplete={() => setHasQueueItems(false)}
      />
    );
  }

  if (!isVisible || !status) {
    return null;
  }

  const progress = status.onboarding_total && status.onboarding_total > 0
    ? Math.round((status.onboarding_progress || 0) / status.onboarding_total * 100)
    : 0;

  const lastUpdateMs = new Date(
    status.updated_at || status.onboarding_started_at || Date.now()
  ).getTime();
  const minutesSinceLastUpdate = Math.max(0, Math.floor((Date.now() - lastUpdateMs) / 60000));
  const isStalled = status.onboarding_status === "in_progress" && minutesSinceLastUpdate >= 15;

  const getStatusIcon = () => {
    if (isStalled) {
      return <XCircle className="h-5 w-5 text-destructive" />;
    }

    switch (status.onboarding_status) {
      case "in_progress":
      case "pending":
        return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "failed":
        return <XCircle className="h-5 w-5 text-destructive" />;
      default:
        return <Sparkles className="h-5 w-5 text-primary" />;
    }
  };

  const formatDuration = (startedAt: string | null, completedAt: string | null): string => {
    if (!startedAt) return "";
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const durationMs = end - start;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusMessage = () => {
    if (isStalled) {
      return `No progress updates for ${minutesSinceLastUpdate} minutes. The generator likely stopped. Try refreshing, or contact support to restart onboarding.`;
    }

    switch (status.onboarding_status) {
      case "pending":
        return "Preparing to generate your website content...";
      case "in_progress":
        const elapsed = status.onboarding_started_at 
          ? `(${formatDuration(status.onboarding_started_at, null)} elapsed)`
          : "";
        return `${status.onboarding_current_step || "Generating content..."} ${elapsed}`;
      case "completed":
        const duration = formatDuration(status.onboarding_started_at, status.onboarding_completed_at);
        return `🎉 Your website content has been generated successfully! ${duration ? `Completed in ${duration}.` : ""}`;
      case "failed":
        return "Content generation encountered an issue. Please try again or contact support.";
      default:
        return "Processing...";
    }
  };

  const getBannerStyles = () => {
    if (isStalled) {
      return "bg-destructive/10 border-destructive/20";
    }

    switch (status.onboarding_status) {
      case "completed":
        return "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800";
      case "failed":
        return "bg-destructive/10 border-destructive/20";
      default:
        return "bg-primary/5 border-primary/20";
    }
  };

  return (
    <div className={`rounded-lg border p-4 mb-6 ${getBannerStyles()}`}>
      <div className="flex items-start gap-3">
        {getStatusIcon()}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-sm">
              {isStalled
                ? "Generation Stalled"
                : status.onboarding_status === "completed" 
                ? "Content Generated!" 
                : status.onboarding_status === "failed"
                ? "Generation Failed"
                : "Generating Your Website"}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {getStatusMessage()}
          </p>
          
          {status.onboarding_status === "in_progress" && status.onboarding_total && status.onboarding_total > 0 && (
            <div className="mt-3 space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {status.onboarding_progress || 0} of {status.onboarding_total} items completed ({progress}%)
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
